import { SfxToggle, useSfx } from '@slingu/ui';
import { useMemo, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL } from './vocabulary.js';

const HIGH_SCORE_ID = 'word-search';

export type WordSearchProps = {
  onExit: (result: { score: number; found: number; total: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

const GRID = 10;          // 10x10 grid
const WORD_COUNT = 6;
const XP_PER_WORD = 18;

type Placement = {
  word: string;          // uppercase (no spaces)
  promptPt: string;
  row: number;
  col: number;
  dRow: -1 | 0 | 1;
  dCol: -1 | 0 | 1;
  found: boolean;
};

type Selection = { row: number; col: number }[];

/**
 * Word Search — caça-palavras clássico. 10×10 grid com 6 palavras em inglês
 * escondidas em horizontal/vertical/diagonal. O jogador arrasta o dedo
 * sobre as letras pra selecionar; se formar uma palavra da lista, vira
 * verde. Ache todas = win.
 */
export function WordSearch({ onExit }: WordSearchProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [grid, setGrid] = useState<string[][]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [selection, setSelection] = useState<Selection>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set());
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function generatePuzzle() {
    // Pick candidate words: length 3..8, alphabetic only
    const candidates = LEGACY_POOL
      .filter((p) => /^[a-zA-Z]+$/.test(p.correct) && p.correct.length >= 3 && p.correct.length <= 8);
    const wordsToPlace = shuffle(candidates).slice(0, WORD_COUNT * 2); // double pool for retry placement

    const g: string[][] = Array.from({ length: GRID }, () =>
      Array.from({ length: GRID }, () => ''),
    );
    const placements: Placement[] = [];

    const directions: Array<[-1 | 0 | 1, -1 | 0 | 1]> = [
      [0, 1],    // →
      [1, 0],    // ↓
      [1, 1],    // ↘
      [-1, 1],   // ↗
    ];

    for (const pair of wordsToPlace) {
      if (placements.length >= WORD_COUNT) break;
      const w = pair.correct.toUpperCase();
      let placed = false;
      for (let attempt = 0; attempt < 60 && !placed; attempt++) {
        const [dr, dc] = directions[Math.floor(Math.random() * directions.length)]!;
        const row = Math.floor(Math.random() * GRID);
        const col = Math.floor(Math.random() * GRID);
        // Check fits
        const endR = row + dr * (w.length - 1);
        const endC = col + dc * (w.length - 1);
        if (endR < 0 || endR >= GRID || endC < 0 || endC >= GRID) continue;
        let ok = true;
        for (let i = 0; i < w.length; i++) {
          const r = row + dr * i;
          const c = col + dc * i;
          const existing = g[r]?.[c];
          if (existing !== '' && existing !== w[i]) { ok = false; break; }
        }
        if (!ok) continue;
        // Place
        for (let i = 0; i < w.length; i++) {
          const r = row + dr * i;
          const c = col + dc * i;
          g[r]![c] = w[i]!;
        }
        placements.push({
          word: w,
          promptPt: pair.promptPt ?? pair.correct,
          row,
          col,
          dRow: dr,
          dCol: dc,
          found: false,
        });
        placed = true;
      }
    }

    // Fill empty cells with random letters
    for (let r = 0; r < GRID; r++) {
      for (let c = 0; c < GRID; c++) {
        if (g[r]![c] === '') {
          g[r]![c] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        }
      }
    }

    setGrid(g);
    setPlacements(placements);
    setFoundCells(new Set());
    setScore(0);
  }

  function startGame() {
    generatePuzzle();
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function keyOf(r: number, c: number) { return `${r},${c}`; }

  function isStraightLine(sel: Selection): { dr: number; dc: number; ok: boolean } {
    if (sel.length < 2) return { dr: 0, dc: 0, ok: true };
    const first = sel[0]!;
    const second = sel[1]!;
    const dr = Math.sign(second.row - first.row);
    const dc = Math.sign(second.col - first.col);
    for (let i = 1; i < sel.length; i++) {
      const prev = sel[i - 1]!;
      const cur = sel[i]!;
      if (cur.row - prev.row !== dr || cur.col - prev.col !== dc) {
        return { dr, dc, ok: false };
      }
    }
    return { dr, dc, ok: true };
  }

  function startSelect(r: number, c: number) {
    if (foundCells.has(keyOf(r, c))) return;
    setIsSelecting(true);
    setSelection([{ row: r, col: c }]);
    sfxRef.current.play('tap');
  }

  function extendSelect(r: number, c: number) {
    if (!isSelecting) return;
    if (selection.length > 0 && selection[selection.length - 1]!.row === r && selection[selection.length - 1]!.col === c) return;
    // Only accept if it remains a straight line with the first cell
    const first = selection[0];
    if (!first) return;
    const dr = Math.sign(r - first.row);
    const dc = Math.sign(c - first.col);
    if (dr === 0 && dc === 0) return;
    if (Math.abs(r - first.row) !== Math.abs(c - first.col) && dr !== 0 && dc !== 0) return; // must be 8-way
    // Build fresh selection from first to (r,c)
    const len = Math.max(Math.abs(r - first.row), Math.abs(c - first.col)) + 1;
    const fresh: Selection = [];
    for (let i = 0; i < len; i++) {
      fresh.push({ row: first.row + dr * i, col: first.col + dc * i });
    }
    if (fresh.some((p) => p.row < 0 || p.row >= GRID || p.col < 0 || p.col >= GRID)) return;
    setSelection(fresh);
  }

  function endSelect() {
    if (!isSelecting) return;
    setIsSelecting(false);
    // Check match
    const { ok } = isStraightLine(selection);
    if (!ok || selection.length < 2) {
      setSelection([]);
      return;
    }
    const word = selection.map((p) => grid[p.row]![p.col]!).join('');
    const reversed = word.split('').reverse().join('');
    const match = placements.find((pl) => !pl.found && (pl.word === word || pl.word === reversed));
    if (match) {
      // Mark cells as found
      const newFound = new Set(foundCells);
      for (const p of selection) newFound.add(keyOf(p.row, p.col));
      setFoundCells(newFound);
      setPlacements((prev) => prev.map((pl) => (pl === match ? { ...pl, found: true } : pl)));
      setScore((s) => s + XP_PER_WORD);
      sfxRef.current.play('xp-gain');
      // Check win
      const allFound = placements.every((pl) => pl === match || pl.found);
      if (allFound) {
        window.setTimeout(() => {
          const finalScore = score + XP_PER_WORD; // setScore ainda não commitado
          const result = commitScore(HIGH_SCORE_ID, finalScore);
          setHighScore(result.best);
          setNewRecord(result.isNew);
          setPhase('over');
          sfxRef.current.play('level-up');
        }, 900);
      }
    } else {
      sfxRef.current.play('wrong');
    }
    setSelection([]);
  }

  const selKeys = useMemo(() => new Set(selection.map((p) => keyOf(p.row, p.col))), [selection]);

  // Shared pointer handlers — map clientX/clientY → grid cell
  const gridRef = useRef<HTMLDivElement | null>(null);
  function cellFromEvent(e: React.PointerEvent): { r: number; c: number } | null {
    const el = gridRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const cellW = rect.width / GRID;
    const cellH = rect.height / GRID;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const c = Math.floor(x / cellW);
    const r = Math.floor(y / cellH);
    if (r < 0 || r >= GRID || c < 0 || c >= GRID) return null;
    return { r, c };
  }

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExit({ score, found: placements.filter((p) => p.found).length, total: placements.length, xp: score })}
        aria-label="Fechar jogo"
      >×</button>
      <div className="sl-flappy__sfx"><SfxToggle variant="icon" /></div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2><span aria-hidden>🔍</span> Word Search</h2>
          <p>
            Arraste o dedo pelas letras pra formar a palavra. Horizontal,
            vertical e diagonal — pra frente ou pra trás. Ache as 6 e ganhe
            XP. Palavras sorteadas do pool de 1000+.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { setNewRecord(false); startGame(); }}>Começar</button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' ? (
        <div className="sl-wsearch">
          <div className="sl-wbuilder__hud">
            <span>Achadas {placements.filter((p) => p.found).length}/{placements.length}</span>
            <span className="sl-wbuilder__hud-item--xp">{score} XP</span>
          </div>

          <div
            className="sl-wsearch__grid"
            ref={gridRef}
            style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)` }}
            onPointerDown={(e) => {
              e.preventDefault();
              const cell = cellFromEvent(e);
              if (cell) startSelect(cell.r, cell.c);
            }}
            onPointerMove={(e) => {
              if (!isSelecting) return;
              e.preventDefault();
              const cell = cellFromEvent(e);
              if (cell) extendSelect(cell.r, cell.c);
            }}
            onPointerUp={() => endSelect()}
            onPointerCancel={() => { setIsSelecting(false); setSelection([]); }}
          >
            {grid.map((row, r) =>
              row.map((ch, c) => {
                const k = keyOf(r, c);
                const isFound = foundCells.has(k);
                const isSel = selKeys.has(k);
                return (
                  <div
                    key={k}
                    className={`sl-wsearch__cell${isSel ? ' sl-wsearch__cell--sel' : ''}${isFound ? ' sl-wsearch__cell--found' : ''}`}
                  >
                    {ch}
                  </div>
                );
              }),
            )}
          </div>

          <ul className="sl-wsearch__list">
            {placements.map((pl, i) => (
              <li key={i} className={`sl-wsearch__word${pl.found ? ' sl-wsearch__word--found' : ''}`}>
                <span className="sl-wsearch__word-pt">{pl.promptPt}</span>
                <span className="sl-wsearch__word-en">{pl.found ? pl.word : '?'.repeat(pl.word.length)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : 'Caçou!'}</h2>
          <p>
            Palavras: <strong>{placements.filter((p) => p.found).length}</strong>/{placements.length}<br />
            XP: <strong>{score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => onExit({ score, found: placements.filter((p) => p.found).length, total: placements.length, xp: score })}
          >Voltar pro app</button>
        </div>
      ) : null}
    </div>
  );
}
